import { getPageIdentityAliases, resolvePageIdentity } from '@/common/utils/resolvePageIdentity'
import { resolvePagesDataStoreKey } from '@/common/utils/resolvePageStoreEntry'

export interface DialogCloseParentContext {
  storeKey: string
  registryFormId: string
}

export function resolvePenultimateDialogId(
  dialogIds: string[] | undefined | null,
  closingDialogId?: string | null,
): string {
  const ids = Array.isArray(dialogIds)
    ? dialogIds.map((id) => String(id ?? '').trim()).filter(Boolean)
    : []
  if (ids.length === 0) return ''
  if (closingDialogId) {
    const closing = String(closingDialogId).trim()
    const idx = ids.lastIndexOf(closing)
    if (idx > 0) return ids[idx - 1]
  }
  if (ids.length >= 2) return ids[ids.length - 2]
  return ''
}

export function resolveParentSlotAtDialogOpen(
  appStore: { dialogIds?: string[]; currentPageId?: string | null } | undefined,
  pageDataSlot?: string | null,
  parentPageId?: string | null,
  options?: { windowInstanceId?: string | null; registryFormId?: string | null },
): string {
  const ids = appStore?.dialogIds ?? []
  if (ids.length > 0) {
    const top = String(ids[ids.length - 1] ?? '').trim()
    if (top) return top
  }

  const windowInstanceId = String(options?.windowInstanceId ?? '').trim()
  const registryFormId = String(options?.registryFormId ?? '').trim()
  const fromDeps = String(pageDataSlot ?? '').trim()

  if (
    fromDeps &&
    registryFormId &&
    fromDeps === registryFormId &&
    windowInstanceId &&
    windowInstanceId !== fromDeps
  ) {
    return windowInstanceId
  }

  if (fromDeps) return fromDeps

  const parent = String(parentPageId ?? appStore?.currentPageId ?? '').trim()
  if (parent && parent !== 'home') return parent
  return ''
}

export function resolveDialogCloseParentStoreKey(
  appStore: {
    pagesData?: Record<string, unknown>
    dialogIds?: string[]
    currentPageId?: string | null
  },
  dialogConfig?: Record<string, unknown> | null,
  options?: { closingDialogId?: string | null },
): string {
  const pagesData = (appStore?.pagesData ?? {}) as Record<string, unknown>
  const rawParent = String(
    dialogConfig?.pageDataSlot ?? dialogConfig?.onCloseDialogParentSlotId ?? '',
  ).trim()
  const penultimate = resolvePenultimateDialogId(appStore?.dialogIds, options?.closingDialogId)

  if (penultimate && pagesData[penultimate]) {
    if (!rawParent) return penultimate
    const penPage = pagesData[penultimate] as Record<string, unknown>
    const aliases = getPageIdentityAliases(penPage as Record<string, any>, { slotId: penultimate })
    if (aliases.includes(rawParent)) return penultimate
  }

  if (rawParent || penultimate) {
    const resolved = resolvePagesDataStoreKey(pagesData, {
      pageDataSlot: rawParent || undefined,
      registryFormId: rawParent || undefined,
      formId: rawParent || undefined,
      topDialogId: penultimate || undefined,
      currentPageId: appStore?.currentPageId ?? undefined,
    })
    if (resolved && pagesData[resolved]) return resolved
  }

  if (penultimate && pagesData[penultimate]) return penultimate

  const currentPageId = String(appStore?.currentPageId ?? '').trim()
  if (currentPageId && currentPageId !== 'home' && pagesData[currentPageId]) {
    return currentPageId
  }

  return rawParent
}

export function resolveDialogCloseParentContext(
  appStore: {
    pagesData?: Record<string, unknown>
    dialogIds?: string[]
    currentPageId?: string | null
  },
  dialogConfig?: Record<string, unknown> | null,
  options?: { closingDialogId?: string | null },
): DialogCloseParentContext {
  const storeKey = resolveDialogCloseParentStoreKey(appStore, dialogConfig, options)
  const parentPage = storeKey ? (appStore.pagesData as Record<string, unknown>)?.[storeKey] : undefined
  const registryFormId =
    resolvePageIdentity(parentPage as Record<string, unknown> | null | undefined, {
      slotId: storeKey,
    }).registryFormId || storeKey

  return { storeKey, registryFormId }
}

function normalizeFormElementFieldName(element: unknown): string {
  if (!element || typeof element !== 'object') return ''
  const record = element as Record<string, unknown>
  const name = record.name
  if (typeof name === 'string') return name.trim()
  if (name && typeof name === 'object' && '_text' in (name as object)) {
    return String((name as { _text?: unknown })._text ?? '').trim()
  }
  const attrs = record._attributes as { name?: string; id?: string } | undefined
  return String(attrs?.name ?? attrs?.id ?? '').trim()
}

function extractFormElementId(element: unknown): string | number | undefined {
  if (!element || typeof element !== 'object') return undefined
  const record = element as Record<string, unknown>
  const attrs = record._attributes as { id?: string | number } | undefined
  const raw = attrs?.id ?? record.id ?? record.elementId
  if (raw == null || raw === '') return undefined
  const asNumber = Number(raw)
  return Number.isFinite(asNumber) && asNumber > 0 ? asNumber : String(raw)
}

function walkFormDefinitionNodes(
  node: unknown,
  panelId: string,
  visitElement: (element: Record<string, unknown>, panelId: string) => void,
): void {
  if (node == null) return
  if (Array.isArray(node)) {
    node.forEach((item) => walkFormDefinitionNodes(item, panelId, visitElement))
    return
  }
  if (typeof node !== 'object') return

  const record = node as Record<string, unknown>
  const nextPanelId = String(
    record.panelId ?? record.id ?? (record._attributes as { id?: unknown } | undefined)?.id ?? panelId,
  ).trim()

  const element = record.element
  if (element && typeof element === 'object') {
    visitElement(element as Record<string, unknown>, nextPanelId || panelId)
  }

  for (const key of ['rows', 'main', 'header', 'footer', 'tr', 'td', 'panelRows']) {
    if (record[key] != null) {
      walkFormDefinitionNodes(record[key], nextPanelId || panelId, visitElement)
    }
  }
}

/** Метаданные элемента родительской формы по имени поля */
export function resolveDialogCloseParentElementMeta(
  parentPageData: Record<string, unknown> | undefined,
  registryFormId: string,
  fieldName: string,
): Record<string, unknown> | undefined {
  const wantedName = String(fieldName ?? '').trim()
  const registry = String(registryFormId ?? '').trim()
  if (!parentPageData || !wantedName || !registry) return undefined

  const formRoot = parentPageData.form as Record<string, unknown> | undefined
  if (!formRoot || typeof formRoot !== 'object') return undefined

  let hit: { element: Record<string, unknown>; panelId: string } | undefined
  const visitElement = (element: Record<string, unknown>, panelId: string) => {
    if (hit) return
    if (normalizeFormElementFieldName(element) !== wantedName) return
    hit = { element, panelId: String(panelId ?? '').trim() }
  }

  const panels = formRoot.panels
  if (panels && typeof panels === 'object' && !Array.isArray(panels)) {
    for (const [panelId, panel] of Object.entries(panels as Record<string, unknown>)) {
      walkFormDefinitionNodes(panel, String(panelId), visitElement)
    }
  }

  walkFormDefinitionNodes(formRoot.main, '', visitElement)
  walkFormDefinitionNodes(formRoot.header, '', visitElement)
  walkFormDefinitionNodes(formRoot.footer, '', visitElement)

  if (!hit) return undefined

  return {
    ...hit.element,
    formId: registry,
    panelId: hit.panelId,
    elementId: extractFormElementId(hit.element),
  }
}

export function findElementForDialogCloseParent(
  elements: Record<string, unknown> | undefined,
  fieldName: string,
  registryFormId: string,
  parentPageData?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const registry = String(registryFormId ?? '').trim()
  if (!fieldName || !registry) return undefined

  const fromFormMeta = resolveDialogCloseParentElementMeta(parentPageData, registry, fieldName)
  if (fromFormMeta) return fromFormMeta

  if (!elements) return undefined

  const registered = elements[fieldName] as Record<string, unknown> | undefined
  if (registered && String(registered.formId ?? '') === registry) {
    return registered
  }

  return undefined
}
